import { Clip, einfuegenAmKopf, imFilmEinfuegen, type Op, type Track } from '@schnitt/shared';
import { splitAt } from './edits';
import type { AssetView } from './api';
import { newId } from './ids';
import { ausDatei, useEditor } from './store';
import { zeitText } from './zeit';

/**
 * **Aus der Mediathek direkt in den Schnitt** (7.156).
 *
 * Sascha, 24. September: „Wenn ich + drücke, fügt es nicht direkt ein nach
 * dem Auswählen in der Mediathek. Und auch wenn ich mehrere auswähle.“ Was
 * über das Plus vom Gerät kam, wurde hochgeladen und aufbereitet -- und lag
 * dann in der Medienliste. Ins Video kam es erst, wenn man jede Datei noch
 * einmal antippte.
 *
 * Jetzt merkt sich die App, was ausgesucht wurde, und legt es in den Schnitt,
 * sobald der Arbeitsdienst es fertig meldet: in der Reihenfolge der Auswahl,
 * am Marker, und was dahinter liegt, rückt nach (`imFilmEinfuegen`). Steht der
 * Marker mitten in einem Clip, wird der dort geteilt (7.197).
 * Hochladen und Aufbereiten dauern; das Blatt muss dafür nicht offen bleiben.
 *
 * Eine Datei, die fertig ist, wartet trotzdem, bis alle vor ihr drin sind --
 * sonst hinge die Reihenfolge davon ab, welche zuerst fertig wird.
 */
export function anDieReihe(ids: readonly string[], kopf: number, spur: string | null = null): void {
  if (ids.length === 0) return;
  useEditor.getState().setEinfuegeWarteschlange({ ids: [...ids], kopf, spur, punkte: {}, eingefuegt: 0 });
}

/** Wie lang ein Medium im Schnitt steht: ein Bild vier Sekunden, sonst die Datei. */
function dauerVon(a: AssetView): number {
  return a.kind === 'image' ? 4 : Math.min(a.durationSec, 3600);
}

function arbeite(): void {
  const s = useEditor.getState();
  const w = s.einfuegeWarteschlange;
  if (!w || w.ids.length === 0) return;

  // Der fertige Anfang der Reihe. Ein Fehlschlag wird übersprungen (die
  // Medienliste sagt, warum); was noch nicht da oder nicht fertig ist, hält
  // alles dahinter auf.
  const rest = [...w.ids];
  const bereit: AssetView[] = [];
  let kaputt = 0;
  while (rest.length) {
    const a = s.assets.find((x) => x.id === rest[0]);
    if (!a) break;
    if (a.status === 'failed' || (a.status === 'ready' && !(dauerVon(a) > 0))) {
      kaputt++;
      rest.shift();
      continue;
    }
    if (a.status !== 'ready') break;
    bereit.push(a);
    rest.shift();
  }
  if (bereit.length === 0 && kaputt === 0) return;

  const fps = s.timeline.settings.fps;
  const punkte = { ...w.punkte };
  const ops: Op[] = [];
  const neue: string[] = [];
  // Die Spur aus der Zeile, in der das `+` stand (7.215) -- wenn sie zur Art
  // des Mediums passt; sonst die erste ihrer Art.
  const gewuenscht = w.spur ? s.timeline.tracks.find((t) => t.id === w.spur) : undefined;
  const bildSpur = gewuenscht?.kind === 'video' ? gewuenscht : s.timeline.tracks.find((t) => t.kind === 'video');
  const tonSpur = gewuenscht?.kind === 'audio' ? gewuenscht : s.timeline.tracks.find((t) => t.kind === 'audio');
  let ab: number | null = null;
  for (const [spur, medien] of [
    [bildSpur, bereit.filter((a) => a.kind !== 'audio')],
    [tonSpur, bereit.filter((a) => a.kind === 'audio')],
  ] as const) {
    if (!spur || medien.length === 0) continue;
    // Beim ersten Mal genau am Marker -- steht er mitten in einem Clip, wird
    // der geteilt (7.197). Was danach kommt, schliesst an das vorige an.
    let punkt = punkte[spur.id];
    let bahn: Track = spur;
    if (punkt === undefined) {
      const amKopf = einfuegenAmKopf(spur, w.kopf, fps);
      punkt = amKopf.punkt;
      if (amKopf.teilen) {
        const teilung = splitAt(s.timeline, punkt, [amKopf.teilen.clip.id]);
        const rechts = teilung.find((o) => o.op === 'addClip') as Extract<Op, { op: 'addClip' }> | undefined;
        const links = teilung.find((o) => o.op === 'updateClip') as Extract<Op, { op: 'updateClip' }> | undefined;
        if (rechts && links) {
          ops.push(...teilung);
          bahn = {
            ...spur,
            clips: [
              ...spur.clips.map((c) => (c.id === amKopf.teilen!.clip.id ? ({ ...c, ...links.patch } as typeof c) : c)),
              rechts.clip,
            ],
          };
        }
      }
    }
    const r = imFilmEinfuegen(bahn, punkt, medien.map(dauerVon));
    // Von hinten nach vorn: so steht nie ein Clip auf dem, der gleich weicht.
    for (const v of [...r.verschoben].sort((x, y) => y.start - x.start)) {
      ops.push({ op: 'moveClip', clipId: v.clipId, toTrackId: spur.id, start: v.start } as Op);
    }
    medien.forEach((a, i) => {
      const clip = Clip.parse({
        id: newId('c'),
        source: { kind: 'asset', assetId: a.id },
        start: r.starts[i]!,
        duration: dauerVon(a),
        label: a.name,
      });
      neue.push(clip.id);
      ops.push(ausDatei({ op: 'addClip', trackId: spur.id, clip } as Op));
    });
    punkte[spur.id] = r.ende;
    if (ab === null || punkt < ab) ab = punkt;
  }

  const eingefuegt = w.eingefuegt + neue.length;
  // Erst die Reihe kürzen, dann schicken: `dispatch` ändert den Stand, und
  // der Zuhörer unten käme sonst mit der alten Reihe ein zweites Mal.
  s.setEinfuegeWarteschlange(rest.length ? { ids: rest, kopf: w.kopf, spur: w.spur, punkte, eingefuegt } : null);
  if (ops.length) {
    s.dispatch(ops);
    s.select(neue);
  }
  if (kaputt > 0) {
    s.toast('error', kaputt === 1 ? 'Eine Datei liess sich nicht aufbereiten und fehlt im Schnitt.' : `${kaputt} Dateien liessen sich nicht aufbereiten und fehlen im Schnitt.`);
  }
  if (neue.length && ab !== null) {
    const wieviel = neue.length === 1 ? `„${bereit[0]!.name}“` : `${neue.length} Dateien`;
    s.toast(
      'success',
      rest.length
        ? `${wieviel} bei ${zeitText(ab)} eingefügt — ${rest.length} ${rest.length === 1 ? 'kommt' : 'kommen'} noch.`
        : `${wieviel} bei ${zeitText(ab)} eingefügt.`,
    );
  }
}

// Abgearbeitet wird, wann immer sich die Medienliste oder die Reihe ändert.
// Die Liste zieht sich neu, sobald der Arbeitsdienst eine Datei fertig meldet.
useEditor.subscribe((jetzt, vorher) => {
  if (jetzt.assets !== vorher.assets || jetzt.einfuegeWarteschlange !== vorher.einfuegeWarteschlange) arbeite();
});
